import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Editor } from '@tiptap/react';
import {
  Sparkles,
  Wand2,
  Minimize2,
  Maximize2,
  Check,
  MessageSquare,
  Loader2,
  ImagePlus,
  HelpCircle,
  FileText,
  Pencil,
  List,
  Table,
  LayoutGrid,
  ChevronRight,
} from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

// ============================================================================
// TYPES
// ============================================================================

export type AIActionType =
  | 'improve'
  | 'simplify'
  | 'expand'
  | 'shorten'
  | 'fix-grammar'
  | 'humanize'
  | 'change-tone'
  | 'custom-prompt'
  | 'generate-hero-image'
  | 'generate-ai-image'  // Opens the full AI Image Generator panel
  | 'generate-faq'
  | 'generate-conclusion'
  | 'generate-list'
  | 'generate-table'
  | 'infographic-diagram'
  | 'edit-with-ai';

export interface AIContextMenuProps {
  editor: Editor | null;
  children: React.ReactNode;
  onAIAction: (action: AIActionType, options?: { tone?: string; prompt?: string }) => Promise<void>;
  isProcessing?: boolean;
  className?: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AIContextMenu({
  editor,
  children,
  onAIAction,
  isProcessing = false,
  className,
}: AIContextMenuProps) {
  const [isCustomPromptOpen, setIsCustomPromptOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);

  // Track selection state
  useEffect(() => {
    if (!editor) return;

    const updateSelectionState = () => {
      const { from, to } = editor.state.selection;
      setHasSelection(from !== to);
    };

    editor.on('selectionUpdate', updateSelectionState);
    updateSelectionState();

    return () => {
      editor.off('selectionUpdate', updateSelectionState);
    };
  }, [editor]);

  const handleAction = useCallback(
    async (action: AIActionType, options?: { tone?: string; prompt?: string }) => {
      if (isProcessing) return;
      // Log AI image generation triggers from context menu
      if (action === 'generate-ai-image' || action === 'generate-hero-image') {
        console.log('[IMAGE_TRACE] [FRONTEND:EDITOR] AI_GEN_TRIGGER - AI image generation triggered from context menu', {
          actionType: action,
          source: 'context-menu',
        });
      }
      await onAIAction(action, options);
    },
    [onAIAction, isProcessing]
  );

  const handleCustomPrompt = useCallback(async () => {
    if (!customPrompt.trim()) return;

    setIsSubmitting(true);
    try {
      await onAIAction('custom-prompt', { prompt: customPrompt });
      setCustomPrompt('');
      setIsCustomPromptOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [customPrompt, onAIAction]);

  const getSelectedText = useCallback(() => {
    if (!editor) return '';
    const { from, to } = editor.state.selection;
    return editor.state.doc.textBetween(from, to, ' ');
  }, [editor]);

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild className={className}>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-64">
          {/* Selection-based AI actions */}
          {hasSelection && (
            <>
              <ContextMenuItem
                onClick={() => handleAction('improve')}
                disabled={isProcessing}
              >
                <Sparkles className="h-4 w-4 mr-2 text-teal" />
                Improve Writing
                {isProcessing && <Loader2 className="h-4 w-4 ml-auto animate-spin" />}
              </ContextMenuItem>

              <ContextMenuItem
                onClick={() => handleAction('fix-grammar')}
                disabled={isProcessing}
              >
                <Check className="h-4 w-4 mr-2 text-green-500" />
                Fix Grammar
              </ContextMenuItem>

              <ContextMenuItem
                onClick={() => handleAction('humanize')}
                disabled={isProcessing}
              >
                <Wand2 className="h-4 w-4 mr-2 text-orange-500" />
                Humanize
              </ContextMenuItem>

              <ContextMenuSub>
                <ContextMenuSubTrigger disabled={isProcessing}>
                  <Pencil className="h-4 w-4 mr-2 text-blue-500" />
                  Transform Text
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-48">
                  <ContextMenuItem onClick={() => handleAction('simplify')}>
                    Simplify
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleAction('expand')}>
                    <Maximize2 className="h-4 w-4 mr-2" />
                    Expand
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleAction('shorten')}>
                    <Minimize2 className="h-4 w-4 mr-2" />
                    Shorten
                  </ContextMenuItem>
                </ContextMenuSubContent>
              </ContextMenuSub>

              <ContextMenuSub>
                <ContextMenuSubTrigger disabled={isProcessing}>
                  <MessageSquare className="h-4 w-4 mr-2 text-teal" />
                  Change Tone
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-48">
                  <ContextMenuItem
                    onClick={() => handleAction('change-tone', { tone: 'professional' })}
                  >
                    Professional
                  </ContextMenuItem>
                  <ContextMenuItem
                    onClick={() => handleAction('change-tone', { tone: 'casual' })}
                  >
                    Casual
                  </ContextMenuItem>
                  <ContextMenuItem
                    onClick={() => handleAction('change-tone', { tone: 'friendly' })}
                  >
                    Friendly
                  </ContextMenuItem>
                  <ContextMenuItem
                    onClick={() => handleAction('change-tone', { tone: 'formal' })}
                  >
                    Formal
                  </ContextMenuItem>
                  <ContextMenuItem
                    onClick={() => handleAction('change-tone', { tone: 'persuasive' })}
                  >
                    Persuasive
                  </ContextMenuItem>
                </ContextMenuSubContent>
              </ContextMenuSub>

              <ContextMenuItem
                onClick={() => setIsCustomPromptOpen(true)}
                disabled={isProcessing}
              >
                <Sparkles className="h-4 w-4 mr-2 text-pink-500" />
                Edit with AI...
              </ContextMenuItem>

              <ContextMenuSeparator />
            </>
          )}

          {/* Content generation actions (always available) */}
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <Sparkles className="h-4 w-4 mr-2 text-teal" />
              Generate Content
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-56">
              <ContextMenuItem
                onClick={() => handleAction('generate-hero-image')}
                disabled={isProcessing}
              >
                <ImagePlus className="h-4 w-4 mr-2 text-teal" />
                Generate Hero Image
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => handleAction('generate-faq')}
                disabled={isProcessing}
              >
                <HelpCircle className="h-4 w-4 mr-2 text-green-500" />
                Generate FAQ
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => handleAction('generate-conclusion')}
                disabled={isProcessing}
              >
                <FileText className="h-4 w-4 mr-2 text-orange-500" />
                Generate Conclusion
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => handleAction('generate-list')}
                disabled={isProcessing}
              >
                <List className="h-4 w-4 mr-2 text-teal" />
                Generate List
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => handleAction('generate-table')}
                disabled={isProcessing}
              >
                <Table className="h-4 w-4 mr-2 text-teal-500" />
                Generate Table
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => handleAction('infographic-diagram')}
                disabled={isProcessing}
              >
                <LayoutGrid className="h-4 w-4 mr-2 text-pink-500" />
                Infographic Diagram
              </ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
        </ContextMenuContent>
      </ContextMenu>

      {/* Custom prompt dialog */}
      <Dialog open={isCustomPromptOpen} onOpenChange={setIsCustomPromptOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit with AI</DialogTitle>
            <DialogDescription>
              Describe how you want to modify the selected text.
            </DialogDescription>
          </DialogHeader>

          {hasSelection && (
            <div className="p-3 bg-muted rounded-md text-sm max-h-32 overflow-y-auto">
              <span className="text-muted-foreground font-medium">Selected text:</span>
              <p className="mt-1 text-foreground">{getSelectedText()}</p>
            </div>
          )}

          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="custom-prompt">Instruction</Label>
              <Textarea
                id="custom-prompt"
                placeholder="e.g., Make this more technical and add examples, translate to Spanish, add statistics..."
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCustomPromptOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCustomPrompt}
              disabled={!customPrompt.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Apply
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AIContextMenu;
